import React, { useState, useEffect } from 'react'
import { EyeIcon, CheckIcon, XMarkIcon, InformationCircleIcon } from '@heroicons/react/24/outline'

interface Course {
  id: string
  title: string
  description: string
  subject: string
  expectedPricePerHour: number
  finalPricePerHour?: number
  duration: number
  status: 'pending' | 'approved' | 'rejected'
  coverImage?: string
  reviewNotes?: string
  reviewedBy?: string
  reviewedAt?: string
  createdAt: string
  updatedAt: string
  teacher: {
    id: string
    user: {
      id: string
      email: string
      firstName: string
      lastName: string
    }
  }
  publisher?: {
    id: string
    email: string
    firstName: string
    lastName: string
  }
}

const AdminCourses = () => {
  const [courses, setCourses] = useState<Course[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null)
  const [statusFilter, setStatusFilter] = useState<'pending' | 'approved' | 'rejected'>('pending')
  const [reviewNotes, setReviewNotes] = useState('')
  const [finalPrice, setFinalPrice] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    fetchCourses()
  }, [statusFilter])

  const fetchCourses = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/courses/admin/all?status=${statusFilter}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setCourses(data)
      } else {
        console.error('获取课程列表失败')
      }
    } catch (error) {
      console.error('获取课程列表失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleReview = async (courseId: string, status: 'approved' | 'rejected') => {
    if (!reviewNotes.trim()) {
      alert('请填写审核意见')
      return
    }

    if (status === 'approved' && !finalPrice.trim()) {
      alert('通过课程时请设置最终价格')
      return
    }

    setSubmitting(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/courses/admin/${courseId}/review`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status,
          reviewNotes,
          finalPricePerHour: status === 'approved' ? parseFloat(finalPrice) : undefined
        })
      })

      if (response.ok) {
        const statusMessage = status === 'approved' ? '通过' : '拒绝'
        alert(`课程审核${statusMessage}成功`)
        setSelectedCourse(null)
        setReviewNotes('')
        setFinalPrice('')
        fetchCourses()
      } else {
        const result = await response.json()
        alert(result.message || '审核失败')
      }
    } catch (error) {
      console.error('审核失败:', error)
      alert('网络错误')
    } finally {
      setSubmitting(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'approved':
        return 'bg-green-100 text-green-800'
      case 'rejected':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return '待审核'
      case 'approved':
        return '已通过'
      case 'rejected':
        return '已拒绝'
      default:
        return '未知'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">课程审核管理</h1>
        
        {/* 状态筛选 */}
        <div className="flex space-x-4">
          <button
            onClick={() => setStatusFilter('pending')}
            className={`px-4 py-2 rounded-md ${
              statusFilter === 'pending'
                ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            待审核
          </button>
          <button
            onClick={() => setStatusFilter('approved')}
            className={`px-4 py-2 rounded-md ${
              statusFilter === 'approved'
                ? 'bg-green-100 text-green-800 border border-green-300'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            已通过
          </button>
          <button
            onClick={() => setStatusFilter('rejected')}
            className={`px-4 py-2 rounded-md ${
              statusFilter === 'rejected'
                ? 'bg-red-100 text-red-800 border border-red-300'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            已拒绝
          </button>
        </div>
      </div>

      {/* 课程列表 */}
      {courses.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">暂无{getStatusText(statusFilter)}的课程</p>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  课程信息
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  教师
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  价格/时长
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  状态
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  提交时间
                </th>
                <th className="relative px-6 py-3">
                  <span className="sr-only">操作</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {courses.map((course) => (
                <tr key={course.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{course.title}</div>
                      <div className="text-sm text-gray-500">{course.subject}</div>
                      <div className="text-xs text-gray-400 mt-1 max-w-xs truncate">
                        {course.description}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {course.teacher.user.firstName} {course.teacher.user.lastName}
                    </div>
                    <div className="text-sm text-gray-500">{course.teacher.user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {course.status === 'approved' && course.finalPricePerHour ? (
                        <>
                          <span>¥{course.finalPricePerHour}/小时</span>
                          <span className="text-xs text-gray-500 ml-2">(最终价格)</span>
                        </>
                      ) : (
                        <>
                          <span>¥{course.expectedPricePerHour}/小时</span>
                          <span className="text-xs text-gray-500 ml-2">(期望价格)</span>
                        </>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">
                      {course.duration}小时
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(course.status)}`}>
                      {getStatusText(course.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(course.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => {
                        setSelectedCourse(course)
                        setReviewNotes('')
                        // 自动计算期望价格+20%作为最终价格
                        const suggestedPrice = (course.expectedPricePerHour * 1.2).toFixed(2)
                        setFinalPrice(suggestedPrice)
                      }}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 课程详情弹窗 */}
      {selectedCourse && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="max-h-96 overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">课程详情</h3>
                <button
                  onClick={() => {
                    setSelectedCourse(null)
                    setReviewNotes('')
                    setFinalPrice('')
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* 课程基本信息 */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h4 className="font-medium text-gray-900 mb-3">课程信息</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">课程标题：</span>
                    <span className="ml-2 font-medium">{selectedCourse.title}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">科目：</span>
                    <span className="ml-2">{selectedCourse.subject}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">期望价格：</span>
                    <span className="ml-2">¥{selectedCourse.expectedPricePerHour}/小时</span>
                  </div>
                  <div>
                    <span className="text-gray-500">课程时长：</span>
                    <span className="ml-2">{selectedCourse.duration}小时</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-500">课程描述：</span>
                    <p className="ml-2 mt-1 whitespace-pre-wrap">{selectedCourse.description}</p>
                  </div>
                </div>
              </div>

              {/* 教师信息 */}
              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <h4 className="font-medium text-gray-900 mb-3">教师信息</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">姓名：</span>
                    <span className="ml-2">{selectedCourse.teacher.user.firstName} {selectedCourse.teacher.user.lastName}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">邮箱：</span>
                    <span className="ml-2">{selectedCourse.teacher.user.email}</span>
                  </div>
                </div>
              </div>

              {/* 审核历史 */}
              {selectedCourse.reviewNotes && (
                <div className="bg-yellow-50 p-4 rounded-lg mb-6">
                  <h4 className="font-medium text-gray-900 mb-3">审核记录</h4>
                  <div className="text-sm">
                    <div className="mb-2">
                      <span className="text-gray-500">审核时间：</span>
                      {selectedCourse.reviewedAt && formatDate(selectedCourse.reviewedAt)}
                    </div>
                    <div>
                      <span className="text-gray-500">审核意见：</span>
                      <p className="mt-1 whitespace-pre-wrap">{selectedCourse.reviewNotes}</p>
                    </div>
                    {selectedCourse.finalPricePerHour && (
                      <div className="mt-2">
                        <span className="text-gray-500">最终价格：</span>
                        <span className="ml-2">¥{selectedCourse.finalPricePerHour}/小时</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 审核操作 */}
              {selectedCourse.status === 'pending' && (
                <div className="bg-white border border-gray-200 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">审核操作</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        最终价格（¥/小时）*
                      </label>
                      <div className="space-y-2">
                        <div className="text-sm text-gray-600 bg-blue-50 p-2 rounded">
                          <span className="font-medium">价格计算：</span>
                          期望价格 ¥{selectedCourse.expectedPricePerHour} + 20% = 
                          <span className="font-medium text-blue-600"> ¥{(selectedCourse.expectedPricePerHour * 1.2).toFixed(2)}</span>
                          （建议价格）
                        </div>
                        <input
                          type="number"
                          value={finalPrice}
                          onChange={(e) => setFinalPrice(e.target.value)}
                          min="0"
                          step="0.01"
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                          placeholder="请设置最终价格"
                        />
                        <p className="text-xs text-gray-500">
                          系统已自动设置为期望价格+20%，您可以根据需要调整
                        </p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        审核意见 *
                      </label>
                      <textarea
                        value={reviewNotes}
                        onChange={(e) => setReviewNotes(e.target.value)}
                        rows={3}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                        placeholder="请填写审核意见..."
                      />
                    </div>
                    <div className="flex space-x-4">
                      <button
                        onClick={() => handleReview(selectedCourse.id, 'approved')}
                        disabled={submitting || !reviewNotes.trim() || !finalPrice.trim()}
                        className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:bg-gray-400 flex items-center"
                      >
                        <CheckIcon className="h-4 w-4 mr-2" />
                        {submitting ? '审核中...' : '通过'}
                      </button>
                      <button
                        onClick={() => handleReview(selectedCourse.id, 'rejected')}
                        disabled={submitting || !reviewNotes.trim()}
                        className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:bg-gray-400 flex items-center"
                      >
                        <XMarkIcon className="h-4 w-4 mr-2" />
                        {submitting ? '审核中...' : '拒绝'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminCourses
